import React, {useCallback, useEffect, useState} from 'react'
import {DatabaseInfo, DatasetFilterInput} from '@/generated/graphql'
import Grid from '@mui/material/GridLegacy'
import {
    FormControl, IconButton,
    MenuItem,
    Select
} from '@mui/material'
import styled from 'styled-components'
import {useLazyQuery, useMutation} from '@apollo/client'
import StyledTextField from '@/components/pages/NewDatasetJobPage/StyledTextField'
import Typography from '@mui/material/Typography'
import {
    mapFilersToPhenoFilterInput,
    mapVariantFiltersToFilterOptions,
    mapVariantInfoFiltersToFilterOptions
} from '@/utils/datasetFilterUtils'
import {LoadingButton} from '@mui/lab'
import {useNavigate} from 'react-router-dom'
import RemainingSampleCount from '@/components/pages/NewDatasetJobPage/RemainingSampleCount'
import {DatasetFilterOptions} from '@/components/pages/NewDatasetJobPage/types'
import {ROUTES} from '@/utils/routes'
import StyledEndButtonContainer from '@/components/common/StyledEndButtonContainer'
import JobErrorModal from '@/components/common/JobErrorModal'
import ArrowBackSharpIcon from '@mui/icons-material/ArrowBackSharp'
import DatasetFilterSection from '@/components/pages/NewDatasetJobPage/DatasetFilterSection'
import {FILTER_OPTIONS_QUERY, REMAINING_SAMPLE_COUNT, SUBMIT_DATASET_JOB} from '@/graphql/query/queries'
import {StyledInputLabel} from '@/components/common/CustomStyledComponents'

const StyledInputDiv = styled.div`
    margin-top: 1em;
`

const StyledStickyDiv = styled.div`
    position: sticky;
    top: 2%;
`

type NewJobPageProps = {
    databaseList: DatabaseInfo[]
}

const NewJobPage = ({databaseList}: NewJobPageProps) => {
    const [databaseId, setDatabaseId] = useState<string>('')
    const [datasetName, setDatasetName] = useState<string>('')
    const [datasetVersion, setDatasetVersion] = useState<string>('')
    const [datasetDescription, setDatasetDescription] = useState<string>('')

    const [selectedPhenoFilters, setSelectedPhenoFilters] = useState<DatasetFilterOptions>({
        boolFilters: {},
        numFilters: {},
        stringFilters: {}
    })
    const [selectedVariantFormatFilters, setSelectedVariantFormatFilters] = useState<DatasetFilterOptions>({
        boolFilters: {},
        numFilters: {},
        stringFilters: {}
    })
    const [selectedVariantFrequenciesFilters, setSelectedVariantFrequenciesFilters] = useState<DatasetFilterOptions>({
        boolFilters: {},
        numFilters: {},
        stringFilters: {}
    })
    const [selectedVariantInfoFilters, setSelectedVariantInfoFilters] = useState<DatasetFilterOptions>({
        boolFilters: {},
        numFilters: {},
        stringFilters: {}
    })

    const [errors, setErrors] = useState({
        databaseErr: false,
        datasetNameErr: false,
        datasetVersionErr: false,
        datasetDescriptionErr: false,
    })

    const [jobErrorModalOpen, setJobErrorModalOpen] = useState<boolean>(false)

    const navigate = useNavigate()

    const [filterQuery, {loading: filtersLoading, error: filterError, data: filters}] = useLazyQuery(FILTER_OPTIONS_QUERY)
    const [remainingSampleCountQuery, {loading: remainingSampleCountLoading, error: remainingSampleCountError, data: remainingSampleCount}] = useLazyQuery(REMAINING_SAMPLE_COUNT)
    const [submitJob, {loading: submitJobLoading, error: submitJobError}] = useMutation(SUBMIT_DATASET_JOB)

    const onBoolFilterSelect = useCallback((filterName: string, checked: boolean, filterVal: boolean, setterFn: (prev: any) => void) => {
        if(checked) {
            setterFn((prev: DatasetFilterOptions) => (
                {...prev, boolFilters: {...prev.boolFilters, [filterName]: filterVal}}
            ))
        } else {
            setterFn((prev: DatasetFilterOptions) => {
                const newBoolFilters = {...prev.boolFilters}
                delete newBoolFilters[filterName]
                return {...prev, boolFilters: newBoolFilters}
            })
        }
    }, [])

    const onNumFilterSelect = useCallback((filterName: string, checked: boolean, filterVal: any, setterFn: (prev: any) => void) => {
        if (checked) {
            setterFn((prev: DatasetFilterOptions) => (
                {...prev, numFilters: {...prev.numFilters, [filterName]: filterVal}}
            ))
        } else {
            setterFn((prev: DatasetFilterOptions) => {
                const newNumFilters = {...prev.numFilters}
                delete newNumFilters[filterName]
                return {...prev, numFilters: newNumFilters}
            })
        }
    }, [])

    const onStringFilterSelect = useCallback((filterName: string, checked: boolean, filterVal: any, setterFn: (prev: any) => void) => {
        if (checked) {
            setterFn((prev: DatasetFilterOptions) => (
                {...prev, stringFilters: {...prev.stringFilters, [filterName]: filterVal}}
            ))
        } else {
            setterFn((prev: DatasetFilterOptions) => {
                const newStringFilters = {...prev.stringFilters}
                delete newStringFilters[filterName]
                return {...prev, stringFilters: newStringFilters}
            })
        }
    }, [])

    const handleCreateJob = useCallback(async () => {
        const inputErrors = {
            databaseErr: false,
            datasetNameErr: false,
            datasetVersionErr: false,
            datasetDescriptionErr: false,
        }
        if(!databaseId) {
            inputErrors.databaseErr = true
        }
        if(!datasetName) {
            inputErrors.datasetNameErr = true
        }
        if(!datasetVersion) {
            inputErrors.datasetVersionErr = true
        }
        if(!datasetDescription) {
            inputErrors.datasetDescriptionErr = true
        }
        if(Object.values(inputErrors).some(err => err)) {
            setErrors(inputErrors)
            return
        }

        const filterInput: DatasetFilterInput = mapFilersToPhenoFilterInput(selectedPhenoFilters, selectedVariantFormatFilters, selectedVariantFrequenciesFilters, selectedVariantInfoFilters)
        await submitJob({variables: {databaseId, datasetName, datasetVersion, datasetDescription, filters: filterInput}})

        navigate(ROUTES.JOBS_DATASET)
    }, [databaseId, datasetName, datasetVersion, datasetDescription, navigate, selectedPhenoFilters, selectedVariantFormatFilters, selectedVariantFrequenciesFilters, selectedVariantInfoFilters])

    useEffect(() => {
        if (databaseId) {
            filterQuery({variables: {databaseId}}).then(() => {
                // reset filters
                setSelectedPhenoFilters({
                    boolFilters: {},
                    numFilters: {},
                    stringFilters: {}
                })
                setSelectedVariantFormatFilters({
                    boolFilters: {},
                    numFilters: {},
                    stringFilters: {}
                })
                setSelectedVariantFrequenciesFilters({
                    boolFilters: {},
                    numFilters: {},
                    stringFilters: {}
                })
                setSelectedVariantInfoFilters({
                    boolFilters: {},
                    numFilters: {},
                    stringFilters: {}
                })
            })
        }
    }, [databaseId])

    useEffect(() => {
        if(submitJobError) {
            setJobErrorModalOpen(true)
        }
    }, [submitJobError])

    useEffect(() => {
        if(databaseId) {
            const filterInput: DatasetFilterInput = mapFilersToPhenoFilterInput(selectedPhenoFilters, selectedVariantFormatFilters, selectedVariantFrequenciesFilters, selectedVariantInfoFilters)

            remainingSampleCountQuery({variables: {databaseId, filter: filterInput }})
        }
    }, [databaseId, selectedPhenoFilters, selectedVariantFormatFilters, selectedVariantFrequenciesFilters, selectedVariantInfoFilters])

    return <>
        <Grid container spacing={2} marginY={2}>
            <Grid item xs={12} paddingTop="0 !important;">
                <IconButton size="small" color="primary" onClick={() => navigate(ROUTES.JOBS_DATASET)}>
                    <ArrowBackSharpIcon/>
                </IconButton>
            </Grid>
            <Grid item xs={4}>
                <StyledStickyDiv>
                    <FormControl fullWidth size="small" required>
                        <StyledInputLabel id="database-select-label" error={errors.databaseErr}>Database</StyledInputLabel>
                        <Select
                            id="database-select"
                            labelId="database-select-label"
                            label="Database"
                            variant="outlined"
                            size="small"
                            value={databaseId}
                            onChange={(evt) => {
                                if(errors.databaseErr) {
                                    setErrors(prevState => ({...prevState, databaseErr: false}))
                                }
                                setDatabaseId(evt.target.value as string)
                            }}
                            error={errors.databaseErr}
                        >
                            {databaseList.filter(db => !!db.database_id).map(db => (
                                <MenuItem key={db.database_id} value={db.database_id!}>
                                    {db.database_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <StyledInputDiv>
                        <StyledTextField
                            fullWidth
                            required
                            id="datasetNameInput"
                            label="Dataset name"
                            size="small"
                            value={datasetName}
                            onChange={(evt: any) => {
                                if(errors.datasetNameErr) {
                                    setErrors(prevState => ({...prevState, datasetNameErr: false}))
                                }
                                setDatasetName(evt.target.value as string)
                            }}
                            error={errors.datasetNameErr}
                        />
                    </StyledInputDiv>
                    <StyledInputDiv>
                        <StyledTextField
                            fullWidth
                            required
                            id="datasetVersionInput"
                            label="Dataset version"
                            size="small"
                            value={datasetVersion}
                            onChange={(evt: any) => {
                                if(errors.datasetVersionErr) {
                                    setErrors(prevState => ({...prevState, datasetVersionErr: false}))
                                }
                                setDatasetVersion(evt.target.value as string)
                            }}
                            error={errors.datasetVersionErr}
                        />
                    </StyledInputDiv>
                    <StyledInputDiv>
                        <StyledTextField
                            multiline
                            fullWidth
                            required
                            id="datasetDescriptionInput"
                            label="Dataset description"
                            size="small"
                            value={datasetDescription}
                            onChange={(evt: any) => {
                                if(errors.datasetDescriptionErr) {
                                    setErrors(prevState => ({...prevState, datasetDescriptionErr: false}))
                                }
                                setDatasetDescription(evt.target.value as string)
                            }}
                            error={errors.datasetDescriptionErr}
                        />
                    </StyledInputDiv>
                    <StyledEndButtonContainer>
                        <LoadingButton
                            disabled={submitJobLoading}
                            loading={submitJobLoading}
                            variant="contained"
                            onClick={handleCreateJob}>
                    Create new Job
                        </LoadingButton>
                    </StyledEndButtonContainer>
                    <Typography paddingTop={0.5} variant="h5" >Remaining sample count: </Typography>

                    <Typography paddingTop={2} variant="h4" display="flex" justifyContent="center">
                        <RemainingSampleCount
                            loading={remainingSampleCountLoading}
                            error={!!remainingSampleCountError}
                            remainingSampleCount={remainingSampleCount?.remainingSampleCount} />
                    </Typography>
                </StyledStickyDiv>
            </Grid>
            <Grid item xs={8} paddingX={4} marginTop={1}>
                <DatasetFilterSection
                    filterSectionHeader="Pheno filters"
                    filterOptions={filters?.filterOptions?.pheno_filter_options}
                    selectedFilters={selectedPhenoFilters}
                    onBoolFilterSelect={(filterName: string, checked: boolean, filterVal: boolean) => onBoolFilterSelect(filterName, checked, filterVal, setSelectedPhenoFilters)}
                    onNumFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onNumFilterSelect(filterName, checked, filterVal, setSelectedPhenoFilters)}
                    onStringFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onStringFilterSelect(filterName, checked, filterVal, setSelectedPhenoFilters)}
                    filtersLoading={filtersLoading}
                    filtersError={!!filterError}/>
                <DatasetFilterSection
                    filterSectionHeader="Variant format filters"
                    filterOptions={mapVariantFiltersToFilterOptions(filters?.filterOptions?.variant_format_filter_options)}
                    selectedFilters={selectedVariantFormatFilters}
                    onBoolFilterSelect={(filterName: string, checked: boolean, filterVal: boolean) => onBoolFilterSelect(filterName, checked, filterVal, setSelectedVariantFormatFilters)}
                    onNumFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onNumFilterSelect(filterName, checked, filterVal, setSelectedVariantFormatFilters)}
                    onStringFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onStringFilterSelect(filterName, checked, filterVal, setSelectedVariantFormatFilters)}
                    filtersLoading={filtersLoading}
                    filtersError={!!filterError}/>
                <DatasetFilterSection
                    filterSectionHeader="Variant frequencies filters"
                    filterOptions={mapVariantFiltersToFilterOptions(filters?.filterOptions?.variant_frequencies_filter_options)}
                    selectedFilters={selectedVariantFrequenciesFilters}
                    onBoolFilterSelect={(filterName: string, checked: boolean, filterVal: boolean) => onBoolFilterSelect(filterName, checked, filterVal, setSelectedVariantFrequenciesFilters)}
                    onNumFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onNumFilterSelect(filterName, checked, filterVal, setSelectedVariantFrequenciesFilters)}
                    onStringFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onStringFilterSelect(filterName, checked, filterVal, setSelectedVariantFrequenciesFilters)}
                    filtersLoading={filtersLoading}
                    filtersError={!!filterError}/>
                <DatasetFilterSection
                    filterSectionHeader="Variant info filters"
                    filterOptions={mapVariantInfoFiltersToFilterOptions(filters?.filterOptions?.variant_info_filter_options)}
                    selectedFilters={selectedVariantInfoFilters}
                    onBoolFilterSelect={(filterName: string, checked: boolean, filterVal: boolean) => onBoolFilterSelect(filterName, checked, filterVal, setSelectedVariantInfoFilters)}
                    onNumFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onNumFilterSelect(filterName, checked, filterVal, setSelectedVariantInfoFilters)}
                    onStringFilterSelect={(filterName: string, checked: boolean, filterVal: any) => onStringFilterSelect(filterName, checked, filterVal, setSelectedVariantInfoFilters)}
                    filtersLoading={filtersLoading}
                    filtersError={!!filterError}/>
            </Grid>
        </Grid>
        <JobErrorModal isOpen={jobErrorModalOpen} message="Unable to create new job" onClose={() => setJobErrorModalOpen(false)} />
    </>
}

export default NewJobPage